import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { Formula } from '../../../types/formula.interface';

interface FormulaToken {
  type: string;
  text: string;
  index: number;
}

@Component({
  selector: 'ces-expression-display',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './expression-display.component.html',
})
export class FormulaExpressionComponent {
  readonly formula = input<Formula | null | undefined>(null);

  readonly tokens = computed<FormulaToken[]>(() => {
    const formula = this.formula();
    if (!formula?.expression) return [];

    const expression = formula.expression.trim();
    const parameters = formula.parameters || [];
    const tokens: FormulaToken[] = [];

    const tokenRegex = /([A-Za-z]\w*(?=\s*\()|[A-Za-z]\w*|\d+(?:\.\d+)?|[+\-*/^]|[(,)]|\s+)/g;
    // Regex explanation:
    // - [A-Za-z]\w*(?=\s*\() matches function names (e.g., "func(")
    // - [A-Za-z]\w* matches variable names (e.g., "var")
    // - \d+(?:\.\d+)? matches numbers (e.g., "123", "45.67")
    // - [+\-*/^] matches operators (e.g., "+", "-", "*", "/")
    // - [(,)] matches parentheses and commas (e.g., "(", ")")
    // - \s+ matches whitespace (e.g., spaces, tabs)
    // - g flag for global search

    let match;
    let tokenIndex = 0;
    while ((match = tokenRegex.exec(expression)) !== null) {
      const text = match[0];

      if (/^\s+$/.test(text)) {
        tokens.push({ type: 'whitespace', text, index: tokenIndex++ });
        continue;
      }

      if (/^[A-Za-z]\w*(?=\s*\()/.test(text)) {
        tokens.push({ type: 'function', text, index: tokenIndex++ });
      } else if (/^[A-Za-z]\w*$/.test(text)) {
        const isParam = parameters?.some(p => p.symbol.toUpperCase() === text.toUpperCase());
        tokens.push({ type: isParam ? 'variable' : 'identifier', text, index: tokenIndex++ });
      } else if (/^\d+(?:\.\d+)?$/.test(text)) {
        tokens.push({ type: 'number', text, index: tokenIndex++ });
      } else if (/^[+\-*/]$/.test(text)) {
        tokens.push({ type: 'operator', text, index: tokenIndex++ });
      } else if (/^[(),]$/.test(text)) {
        tokens.push({ type: 'parenthesis', text, index: tokenIndex++ });
      } else {
        tokens.push({ type: 'unknown', text, index: tokenIndex++ });
      }
    }

    return tokens;
  });
}
